import React, { useState, useEffect, useCallback } from 'react';
import {
  UserIcon,
  CheckCircleIcon,
  XCircleIcon,
  TrashIcon,
  CalendarDaysIcon,
  ArrowRightOnRectangleIcon,
  PhoneIcon,
  EnvelopeIcon,
} from '@heroicons/react/20/solid';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Upload } from 'antd';
import { TWButton, TWModal } from '../../../components/ui';
import { Switch } from '@headlessui/react';
import ModalDelete from '../../../components/modal-delete/modal-delete';
import companyApi from '../../../services/companyApi';
import referralApi from '../../../services/referralApi';
import { Select } from 'antd';
import {
  ArrowLeftIcon,
  ArrowTrendingUpIcon,
  InformationCircleIcon,
  CreditCardIcon,
  Cog6ToothIcon,
  DocumentTextIcon,
  BuildingStorefrontIcon,
  PencilSquareIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';
import userApi from '../../../services/userApi';
import Notification from '../../../components/notification/notification';
import ReactInputMask from 'react-input-mask';
import { useSelector, useDispatch } from 'react-redux';
import { useSpringRef, useTransition, animated } from '@react-spring/web';
import { PlusOutlined, LoadingOutlined } from '@ant-design/icons';
import { SkeletonBlock } from '../../../components/skeleton/skeleton';
import AccountActivitySwitch from './tabs/account-activity-switch';
import AccountInformation from './tabs/account-information-tab';
import Fees from './tabs/fees-tab';
import Settings from './tabs/settings-tab';
import Notes from './tabs/notes-tab';
import Stores from './tabs/stores-tab';
import Connect from './tabs/connect-tab';
import { RoutingLabel } from './company-routing-badge';
import connectApi from '../../../services/connectApi';
import moment from 'moment';
import {
  loginSuccess,
  setRole,
  setRolePermissions,
} from '../../../store/slices/authSlice';
import { setGlobalCompany } from '../../../store/slices/companySlice';
import tokenService from '../../../utils/tokenService';
import './company-detail.css';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const initialsOf = (name) => {
  if (!name) return '?';
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s.charAt(0).toUpperCase())
    .join('');
};

const CompanyDetail = () => {
  const userRoles = useSelector((state) => state.authReducer.roles);
  const isAdmin = userRoles.includes(100);
  const { id } = useParams();
  const navigate = useNavigate();
  const roles = useSelector((state) => state.authReducer.roles);
  const dispatch = useDispatch();
  // Stripe's embedded card on the Account Activity tab takes the theme the
  // way the Connect tab's Support block does.
  const isDark = useSelector((state) => state.commonReducer.isDark);

  const tabs = roles.includes(100)
    ? [
        { name: 'Account Activity', id: 0, icon: ArrowTrendingUpIcon },
        { name: 'Account Information', id: 1, icon: InformationCircleIcon },
        { name: 'Fees', id: 2, icon: CreditCardIcon },
        { name: 'Stores', id: 3, icon: BuildingStorefrontIcon },
        { name: 'Settings', id: 4, icon: Cog6ToothIcon },
        { name: 'Notes', id: 5, icon: DocumentTextIcon },
        { name: 'Connect', id: 6, icon: LinkIcon },
      ]
    : [];

  // `?tab=connect` opens on the Connect tab: the admin Finance page's
  // Connect accounts list links straight to it (owner, 2026-09-24).
  const [searchParams] = useSearchParams();
  const [currentTab, setCurrentTab] = useState(
    searchParams.get('tab') === 'connect' && roles.includes(100) ? 6 : 0,
  );

  const [companyDetail, setCompantDetail] = useState({});
  // Where this company's money routes, read once per company id after
  // Company/Get answers (spec 2026-10-20, admin company page: Account
  // Activity shows a Stripe company's payouts, §3). One read feeds the
  // header's routing badge and tab 0's choice between the Payouts view and
  // the ledger. Kept with the company it was read for (pre-flight m1): an
  // answer about company A is never shown for company B, and until B's own
  // read lands the badge draws nothing and tab 0 draws its loading block. A
  // failed read is `status: null` for that id: the badge draws nothing and
  // the tab draws the ledger, the safe default. Keyed on the id alone:
  // Company/Get runs again after every edit on this page and must not
  // re-read Stripe each time. The Connect tab keeps its own read because it
  // changes the account (Activate, Deactivate, onboarding) — and reports
  // every status it applies through `onStatusChange` below (pre-flight I1),
  // so tab 0 and the badge follow it without a reload.
  const [connect, setConnect] = useState({ companyId: null, status: null });
  const companyId = companyDetail?.id;
  useEffect(() => {
    if (!isAdmin || !companyId) return undefined;
    let active = true;
    (async () => {
      let next = null;
      try {
        next = connectApi.toConnectStatus(await connectApi.getStatus(companyId));
      } catch {
        next = null;
      }
      if (active) setConnect({ companyId, status: next });
    })();
    return () => {
      active = false;
    };
  }, [isAdmin, companyId]);
  const statusLoaded = connect.companyId === companyId;
  const routingStatus = statusLoaded ? connect.status : null;
  const onConnectStatusChange = useCallback(
    (next) => setConnect({ companyId, status: next }),
    [companyId],
  );
  const [userList, setUserList] = useState([]);

  const [form, setForm] = useState({
    id: '',
    ownername: '',
    ownerphone: '',
    owneremail: '',
    logo: '',
    emaildomain: '',
    name: '',
    itemfee: 0,
    users: [],
    isactive: false,
    subscriptionid: '',
    creditcardfeeper: null,
    creditcardfeerate: null,
    sendreport: true,
    issetorders: false,
  });
  const [loading, setLoading] = useState({
    initialLoading: true,
    upsertLoading: false,
    deleteLoading: false,
  });

  const [errors, setErrors] = useState({ code: false, name: false });

  // Davet eden restoranın kodu admin tarafından girilir (write-only — Company/Get
  // uygulanmış kodu döndürmüyor). Şirket upsert'ünden bağımsız, kendi Apply'ı var.
  const [referralCode, setReferralCode] = useState('');
  const [referralApplying, setReferralApplying] = useState(false);

  const [dialogs, setDialogs] = useState({ upsert: false, delete: false });

  const [notf, setNotf] = useState({
    display: false,
    type: 'error',
    message: '',
  });

  const [isNext, setIsNext] = useState(false);
  const transRef = useSpringRef();

  const transitions = useTransition(currentTab, {
    ref: transRef,
    keys: null,
    from: {
      opacity: 0,
      transform: isNext ? 'translate3d(10%,0,0)' : 'translate3d(-10%,0,0)',
    },
    enter: { opacity: 1, transform: 'translate3d(0%,0,0)' },
    leave: {
      opacity: 0,
      transform: isNext ? 'translate3d(-10%,0,0)' : 'translate3d(10%,0,0)',
    },
  });

  const tabComponents = [
    <AccountActivitySwitch
      companyDetail={companyDetail}
      status={routingStatus}
      statusLoaded={statusLoaded}
      setNotf={setNotf}
      isDark={isDark}
    />,
    <AccountInformation
      companyDetail={companyDetail}
      notf={notf}
      setNotf={setNotf}
    />,
    <Fees
      companyDetail={companyDetail}
      setNotf={setNotf}
      updateCompany={() => getCompanyDetail()}
    />,
    <Stores
      companyDetail={companyDetail}
      setNotf={setNotf}
      updateCompany={() => getCompanyDetail()}
    />,
    <Settings
      companyDetail={companyDetail}
      setNotf={setNotf}
      updateCompany={() => getCompanyDetail()}
    />,
    <Notes
      companyDetail={companyDetail}
      setNotf={setNotf}
      updateCompany={() => getCompanyDetail()}
    />,
    <Connect companyDetail={companyDetail} setNotf={setNotf} onStatusChange={onConnectStatusChange} />,
  ];

  useEffect(() => {
    transRef.start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab]);

  const onClickTab = (tab) => {
    setIsNext(tab.id > currentTab);
    setCurrentTab(tab.id);
  };

  const onClickTabMobile = (id) => {
    setIsNext(id > currentTab);
    setCurrentTab(id);
  };

  const onFormChange = (val, key) => {
    setForm((prev) => ({ ...prev, [key]: val }));
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: false }));
    }
  };

  const onFormSubmit = async () => {
    if (!form.name) {
      setErrors((prev) => ({ ...prev, name: true }));
      return;
    }
    setErrors((prev) => ({ ...prev, name: false }));
    try {
      setLoading((prev) => ({ ...prev, upsertLoading: true }));
      const body = { ...form };
      const formData = new FormData();
      Object.keys(body).forEach((key) => {
        if (body[key]) formData.append(key, body[key]);
      });
      const config = { headers: { 'Content-Type': 'multipart/form-data' } };
      const res = await companyApi.upsert(body, config);
      if (res.data.data) {
        setDialogs((prev) => ({ ...prev, upsert: false }));
      }
    } catch (error) {
      setNotf((prev) => ({ ...prev, message: error, display: true }));
    } finally {
      setLoading((prev) => ({ ...prev, upsertLoading: false }));
      await getCompanyDetail();
    }
  };

  const onClickEdit = () => {
    setForm({
      ...companyDetail,
      imagepath: companyDetail.logo,
      users: (companyDetail.companyusers || []).map((x) => x.userid),
    });
    setReferralCode('');
    setDialogs((prev) => ({ ...prev, upsert: true }));
  };

  const onCloseUpsert = () => {
    if (loading.upsertLoading) return;
    setErrors((prev) => ({ ...prev, code: false, name: false }));
    setReferralCode('');
    setDialogs((prev) => ({ ...prev, upsert: false }));
  };

  // Davet eden kodunu bu şirkete bağlar (Referral/ApplyCode { companyid, code }).
  // Save Changes'ten bağımsızdır; kendi başarı/hata bildirimini gösterir.
  const onApplyReferral = async () => {
    const trimmed = referralCode.trim();
    if (!trimmed || referralApplying) return;
    setReferralApplying(true);
    try {
      const res = await referralApi.applyCode({
        companyid: companyDetail.id,
        code: trimmed,
      });
      if (res.data?.data === true) {
        setReferralCode('');
        setNotf((prev) => ({
          ...prev,
          display: true,
          type: 'success',
          message: 'Referral code applied',
        }));
      } else {
        setNotf((prev) => ({
          ...prev,
          display: true,
          type: 'error',
          message: "That code couldn't be applied",
        }));
      }
    } catch (error) {
      setNotf((prev) => ({
        ...prev,
        display: true,
        type: 'error',
        message:
          error?.response?.data?.Message || 'Failed to apply referral code',
      }));
    } finally {
      setReferralApplying(false);
    }
  };

  useEffect(() => {
    getCompanyDetail();
    getUserList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getCompanyDetail = async () => {
    try {
      setLoading((prev) => ({ ...prev, initialLoading: true }));
      const res = await companyApi.getById(id);
      if (res.data) setCompantDetail(res.data.data);
    } catch (error) {
      setNotf((prev) => ({ ...prev, message: error, display: true }));
    } finally {
      setLoading((prev) => ({ ...prev, initialLoading: false }));
    }
  };

  const getUserList = async () => {
    try {
      const list = await userApi.list(`?pageSize=1000&status=10&status=0`);
      if (list.data.data.length > 0) setUserList(list.data.data);
    } catch (error) {
      setNotf((prev) => ({ ...prev, message: error, display: true }));
    }
  };

  const onClickDelete = () => {
    setDialogs((prev) => ({ ...prev, upsert: false, delete: true }));
  };

  const onConfirmDelete = async () => {
    try {
      setLoading((prev) => ({ ...prev, deleteLoading: true }));
      await companyApi.deleteById(companyDetail.id);
    } catch (error) {
      setNotf((prev) => ({ ...prev, message: error, display: true }));
    } finally {
      setDialogs((prev) => ({ ...prev, delete: false }));
      setLoading((prev) => ({ ...prev, deleteLoading: false }));
      navigate(-1);
    }
  };

  const uploadButton = (
    <div className="flex flex-col items-center justify-center gap-1 text-slate-500 dark:text-slate-300">
      {loading.upsertLoading ? (
        <LoadingOutlined />
      ) : (
        <PlusOutlined className="text-lg" />
      )}
      <span className="text-xs">Upload</span>
    </div>
  );

  const rolePermissions = (permissions) => {
    let rolepermissions = [];
    permissions.forEach((permission) => {
      rolepermissions.push(permission.permission.code);
    });
    return rolepermissions;
  };

  const signAsCompany = async (comp) => {
    try {
      setLoading((prev) => ({ ...prev, loginAsCompany: true }));
      const res = await companyApi.signAsCompanyById(comp.id);
      if (res.data.data) {
        if (res.data.data.roles.includes('Owner')) {
          tokenService.updateLocalAccessToken(res.data.data.token);
          const userDetail = await userApi.getUserDetail();

          if (userDetail.status === 200) {
            tokenService.setRole([150]);
            dispatch(loginSuccess(userDetail.data.data));
            dispatch(setGlobalCompany(comp));
            dispatch(setRole([150]));
            dispatch(
              setRolePermissions(
                rolePermissions(res.data.data.permissions[0].rolepermissions),
              ),
            );
            navigate('/dashboard');
          }
        }
      }
    } catch (error) {
      setNotf((prev) => ({ ...prev, message: error, display: true }));
    } finally {
      setLoading((prev) => ({ ...prev, loginAsCompany: false }));
    }
  };

  const isStripeConnected = !!companyDetail?.stripeaccountid;

  return (
    <div className="company-detail-shell min-h-[calc(100vh-4rem)] bg-white px-4 py-6 dark:bg-slate-900/40 sm:px-6 lg:px-8 lg:py-8">
      <div className="mx-auto w-full max-w-7xl space-y-6">
        {/* Back link */}
        <button
          type="button"
          onClick={() => navigate('/dashboard/companies')}
          className="reveal inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 transition hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Companies
        </button>

        {loading.initialLoading ? (
          <>
            <div className="reveal flex flex-col gap-4 rounded-md border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-center gap-4">
                <SkeletonBlock className="h-16 w-16 !rounded-full" />
                <div className="space-y-2">
                  <SkeletonBlock className="h-6 w-48" />
                  <SkeletonBlock className="h-4 w-64" />
                  <SkeletonBlock className="h-4 w-40" />
                </div>
              </div>
              <div className="flex gap-2">
                <SkeletonBlock className="h-10 w-32 rounded-md" />
                <SkeletonBlock className="h-10 w-24 rounded-md" />
              </div>
            </div>
            <div className="reveal flex gap-2 overflow-x-auto">
              {[...Array(6)].map((_, i) => (
                <SkeletonBlock key={i} className="h-10 w-36 rounded-full" />
              ))}
            </div>
            <SkeletonBlock className="h-64 w-full rounded-md" />
          </>
        ) : (
          <>
            {/* Header Card */}
            <header className="reveal flex flex-col gap-5 rounded-md border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800 lg:flex-row lg:items-start lg:justify-between">
              <div className="flex min-w-0 items-start gap-4">
                {companyDetail.logo ? (
                  <img
                    className="h-16 w-16 shrink-0 rounded-md object-cover shadow-sm ring-1 ring-slate-200 dark:ring-slate-700"
                    src={companyDetail.logo}
                    alt={companyDetail.name || 'Company'}
                  />
                ) : (
                  <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-sky-500 to-blue-600 text-base font-semibold text-white shadow-sm">
                    {initialsOf(companyDetail.name)}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-3">
                    <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100 sm:text-3xl">
                      {companyDetail.name || '—'}
                    </h1>
                    {companyDetail.isactive ? (
                      <span className="inline-flex h-6 items-center gap-1 rounded-md bg-emerald-50 px-2.5 text-xs font-semibold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
                        <CheckCircleIcon className="h-3.5 w-3.5" />
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex h-6 items-center gap-1 rounded-md bg-slate-100 px-2.5 text-xs font-semibold text-slate-600 dark:bg-slate-700/60 dark:text-slate-300">
                        <XCircleIcon className="h-3.5 w-3.5" />
                        Inactive
                      </span>
                    )}
                    {isStripeConnected && (
                      <span className="inline-flex h-6 items-center gap-1 rounded-full bg-violet-50 px-2.5 text-xs font-semibold text-violet-700 dark:bg-violet-500/10 dark:text-violet-300">
                        Stripe Connected
                      </span>
                    )}
                    {isAdmin ? (
                      <RoutingLabel model={routingStatus?.paymentModel} />
                    ) : null}
                    {isAdmin && (
                      <button
                        type="button"
                        onClick={() => signAsCompany(companyDetail)}
                        className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 transition hover:text-blue-700 dark:text-sky-400 dark:hover:text-sky-300"
                      >
                        <ArrowRightOnRectangleIcon className="h-4 w-4" />
                        Sign as company
                      </button>
                    )}
                  </div>

                  <dl className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-500 dark:text-slate-400">
                    {companyDetail.ownername && (
                      <div className="flex items-center gap-1.5">
                        <UserIcon className="h-4 w-4 text-slate-400" />
                        <span>{companyDetail.ownername}</span>
                      </div>
                    )}
                    {companyDetail.ownerphone && (
                      <div className="flex items-center gap-1.5">
                        <PhoneIcon className="h-4 w-4 text-slate-400" />
                        <span>{companyDetail.ownerphone}</span>
                      </div>
                    )}
                    {companyDetail.owneremail && (
                      <div className="flex items-center gap-1.5">
                        <EnvelopeIcon className="h-4 w-4 text-slate-400" />
                        <span>{companyDetail.owneremail}</span>
                      </div>
                    )}
                    {companyDetail.createdate && (
                      <div className="flex items-center gap-1.5">
                        <CalendarDaysIcon className="h-4 w-4 text-slate-400" />
                        <span>
                          Created{' '}
                          {moment
                            .utc(companyDetail.createdate)
                            .local()
                            .format('MM/DD/YYYY')}
                        </span>
                      </div>
                    )}
                  </dl>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <TWButton
                  variant="primary"
                  icon={PencilSquareIcon}
                  label="Edit"
                  onClick={onClickEdit}
                />
              </div>
            </header>

            {/* Tabs */}
            {roles.includes(100) && (
              <>
                <div className="reveal sm:hidden">
                  <Select
                    size="large"
                    className="company-detail-select"
                    style={{ width: '100%' }}
                    placeholder="Please select"
                    value={currentTab}
                    onChange={(val) => onClickTabMobile(val)}
                    options={tabs.map((x) => ({ label: x.name, value: x.id }))}
                  />
                </div>
                <nav
                  className="reveal hidden overflow-x-auto sm:block"
                  aria-label="Tabs"
                >
                  <div className="flex flex-wrap gap-2">
                    {tabs.map((tab) => {
                      const isActive = currentTab === tab.id;
                      const TabIcon = tab.icon;
                      return (
                        <button
                          key={tab.id}
                          type="button"
                          onClick={() => onClickTab(tab)}
                          aria-current={isActive ? 'page' : undefined}
                          className={classNames(
                            'inline-flex h-10 items-center gap-2 rounded-full border px-4 text-sm font-semibold transition',
                            isActive
                              ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-sm dark:border-sky-400 dark:bg-sky-500/10 dark:text-sky-300'
                              : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:border-slate-600 dark:hover:bg-slate-700/60',
                          )}
                        >
                          <TabIcon className="h-4 w-4" />
                          {tab.name}
                        </button>
                      );
                    })}
                  </div>
                </nav>

                <div className="reveal">
                  {transitions((style, item) => (
                    <animated.div style={{ ...style }}>
                      {tabComponents[item]}
                    </animated.div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Edit Modal */}
      <TWModal
        open={dialogs.upsert}
        onClose={onCloseUpsert}
        title="Edit Company"
        description="Update company profile and ownership details."
        size="2xl"
        showCloseButton
        dismissible={false}
        bodyClassName="p-0"
        footer={
          <div className="flex items-center justify-between gap-3">
            <TWButton
              variant="danger"
              icon={TrashIcon}
              label="Delete"
              disabled={loading.deleteLoading}
              loading={loading.deleteLoading}
              onClick={onClickDelete}
            />
            <div className="flex items-center gap-2">
              <TWButton
                variant="default"
                label="Cancel"
                disabled={loading.upsertLoading}
                onClick={onCloseUpsert}
              />
              <TWButton
                variant="primary"
                label="Save Changes"
                disabled={loading.upsertLoading}
                loading={loading.upsertLoading}
                onClick={onFormSubmit}
              />
            </div>
          </div>
        }
      >
        {/* Modal body */}
        <div className="space-y-5 ">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Company Logo
            </label>
            <div className="mt-2">
              <Upload
                multiple={false}
                maxCount={1}
                name="avatar"
                onChange={(e) => onFormChange(e.file, 'image')}
                listType="picture-circle"
                className="avatar-uploader"
                beforeUpload={() => false}
              >
                {form.logo ? (
                  <img
                    src={form.logo}
                    alt="avatar"
                    style={{
                      width: '100%',
                      height: '100%',
                      borderRadius: '50%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  uploadButton
                )}
              </Upload>
            </div>
          </div>

          <div>
            <label
              htmlFor="name"
              className="block text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400"
            >
              Company Name
            </label>
            <input
              id="name"
              name="name"
              type="text"
              value={form.name}
              onChange={(e) => onFormChange(e.target.value, 'name')}
              placeholder="e.g. 'Abc Pizza'"
              className={classNames(
                'mt-2 block w-full rounded-md border bg-white px-3 py-2 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 dark:bg-slate-900/70 dark:text-slate-200 dark:placeholder:text-slate-500',
                errors.name
                  ? 'border-rose-400 focus:border-rose-500 focus:ring-rose-500/20 dark:border-rose-500/70'
                  : 'border-slate-200 focus:border-blue-500 focus:ring-blue-500/20 dark:border-slate-600 dark:focus:border-sky-400 dark:focus:ring-sky-400/20',
              )}
            />
            {errors.name && (
              <p className="mt-1 text-xs text-rose-600 dark:text-rose-400">
                Name can not be empty
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor="ownername"
                className="block text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400"
              >
                Owner Name
              </label>
              <input
                id="ownername"
                type="text"
                value={form.ownername}
                onChange={(e) => onFormChange(e.target.value, 'ownername')}
                placeholder="e.g. 'John Doe'"
                className="mt-2 block w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-slate-600 dark:bg-slate-900/70 dark:text-slate-200 dark:placeholder:text-slate-500 dark:focus:border-sky-400 dark:focus:ring-sky-400/20"
              />
            </div>
            <div>
              <label
                htmlFor="ownerphone"
                className="block text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400"
              >
                Owner Phone
              </label>
              <ReactInputMask
                mask="(999) 999 9999"
                maskPlaceholder=" "
                value={form.ownerphone}
                onChange={(e) => onFormChange(e.target.value, 'ownerphone')}
                type="text"
                id="ownerphone"
                className="mt-2 block w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-slate-600 dark:bg-slate-900/70 dark:text-slate-200 dark:placeholder:text-slate-500 dark:focus:border-sky-400 dark:focus:ring-sky-400/20"
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="owneremail"
              className="block text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400"
            >
              Owner E-mail
            </label>
            <input
              id="owneremail"
              type="text"
              value={form.owneremail}
              onChange={(e) => onFormChange(e.target.value, 'owneremail')}
              placeholder="e.g. 'owner@abc.com'"
              className="mt-2 block w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-slate-600 dark:bg-slate-900/70 dark:text-slate-200 dark:placeholder:text-slate-500 dark:focus:border-sky-400 dark:focus:ring-sky-400/20"
            />
          </div>

          <div>
            <label
              htmlFor="referralcode"
              className="block text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400"
            >
              Referral Code
            </label>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Attribute this company to the restaurant that referred them. Enter
              the referrer&apos;s code.
            </p>
            <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
              <input
                id="referralcode"
                type="text"
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onApplyReferral()}
                placeholder="e.g. ABC23XYZ"
                disabled={referralApplying}
                className="block w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-slate-600 dark:bg-slate-900/70 dark:text-slate-200 dark:placeholder:text-slate-500 dark:focus:border-sky-400 dark:focus:ring-sky-400/20"
              />
              <TWButton
                variant="primary"
                label={referralApplying ? 'Applying…' : 'Apply'}
                loading={referralApplying}
                disabled={!referralCode.trim() || referralApplying}
                onClick={onApplyReferral}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Users
            </label>
            <Select
              mode="multiple"
              allowClear
              size="large"
              className="company-detail-select mt-2"
              style={{ width: '100%' }}
              placeholder="Please select"
              value={form.users}
              onChange={(val) => onFormChange(val, 'users')}
              options={userList.map((x) => ({
                label: x.fullname,
                value: x.id,
              }))}
            />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2 dark:border-slate-600">
              <div>
                <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                  Active
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Company is live in the platform
                </p>
              </div>
              <Switch
                checked={form.isactive}
                onChange={(e) => onFormChange(e, 'isactive')}
                className={classNames(
                  form.isactive
                    ? 'bg-blue-600 dark:bg-sky-500'
                    : 'bg-slate-200 dark:bg-slate-600',
                  'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                )}
              >
                <span
                  aria-hidden="true"
                  className={classNames(
                    form.isactive ? 'translate-x-5' : 'translate-x-0',
                    'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                  )}
                />
              </Switch>
            </div>
            <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2 dark:border-slate-600">
              <div>
                <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                  Show Display Inputs
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Expose kitchen/display inputs on orders
                </p>
              </div>
              <Switch
                checked={form.issetorders}
                onChange={(e) => onFormChange(e, 'issetorders')}
                className={classNames(
                  form.issetorders
                    ? 'bg-blue-600 dark:bg-sky-500'
                    : 'bg-slate-200 dark:bg-slate-600',
                  'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                )}
              >
                <span
                  aria-hidden="true"
                  className={classNames(
                    form.issetorders ? 'translate-x-5' : 'translate-x-0',
                    'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                  )}
                />
              </Switch>
            </div>
          </div>
        </div>
      </TWModal>

      <ModalDelete
        title="Company"
        loading={loading.deleteLoading}
        open={dialogs.delete}
        onCloseRequest={() =>
          setDialogs((prev) => ({ ...prev, delete: false }))
        }
        onOk={onConfirmDelete}
        item={companyDetail}
        shouldConfirm={true}
      />
      <Notification
        display={notf.display}
        type={notf.type}
        message={notf.message}
        onClose={() => setNotf((prev) => ({ ...prev, display: false }))}
      />
    </div>
  );
};

export default CompanyDetail;
